import { AxiosResponseHeaders } from "axios";
import {
  CoachReportSummary,
  NCBPReportsResponseSummary,
  NotificationStatusReportSummary,
  OutreachReportSummary,
  PendingBillsSummary,
} from "../../api/services/reports/models";
import {
  EMPTY_VALUE_DASH,
  PENDING_BILLING_REPORT_TYPE_PROGRAM_PROJECT_SITE_CORE_KEY_VALUE,
  REGEX_FILENAME_FROM_CONTENT_DEPOSITION,
} from "../../helpers/constants";
import { KeyValue } from "../../helpers/types/common";
import {
  convertToUserFormat,
  getAmountWithCurrency,
  getFullName,
  getValueOrBlank,
  isNotEmpty,
} from "../../helpers/utils";
import {
  CoachReportSummaryRow,
  NCBPReportsSummaryRow,
  NotificationStatusReportSummaryRow,
  OutreachReportSummaryRow,
  PendingBillsSummaryRow,
} from "./modelsAndSchemas/models";
import { reportsResultsSiteCoreKeys } from "./siteCore/SiteCoreKeys";

export const mapPendingBillingReports = (
  list: PendingBillsSummary[],
  userCountryDateFormat?: string,
): PendingBillsSummaryRow[] => {
  return list.map((item) => ({
    key: item?.key,
    type: item?.type,
    clientName: item?.client?.name,
    clientId: item?.client?.id,
    id: item?.id,
    status: item?.status,
    currency: item?.billingCurrency,
    participantName: isRecordTypeProgram(item?.type)
      ? getFullName(
          item?.participant?.firstName,
          item?.participant?.lastName,
          true,
        )
      : item?.projectName,
    localClientRelationshipOwnerName: getFullName(
      item?.localClientRelationshipOwner?.firstName,
      item?.localClientRelationshipOwner?.lastName,
      true,
    ),
    fees: item?.fees,
    totalTaxInvoiced: item?.totalTaxInvoiced,
    totalExpenseInvoiced: item?.totalExpenseInvoiced,
    totalFeesInvoiced: item?.totalFeesInvoiced,
    totalOutstandingAmount: item?.totalOutstandingAmount,
    billingContact: getFullName(
      item?.billingContact?.firstName,
      item?.billingContact?.lastName,
      true,
    ),
    poNumbers: item?.poNumbers,
    productLineName: item?.productLine?.name,
    programTemplateName: isRecordTypeProgram(item?.type)
      ? item?.programTemplate?.name
      : item?.projectFramework?.name,
    alternateProgramTemplateName: item?.alternateProgramTemplateName,
    hrContact: getFullName(
      item?.hrContact?.firstName,
      item?.hrContact?.lastName,
      true,
    ),
    costCenter: item?.costCenter,
    businessUnit: item?.businessUnit,
    deliveryOffice: item?.deliveryOffice?.name,
    nextView: item?.nextView?.name,
    actualStartDate: convertToUserFormat(
      item?.actualStartDate,
      userCountryDateFormat,
    ),
  }));
};

export const getFileNameFromHeader = (headers: AxiosResponseHeaders) => {
  const disposition = headers["content-disposition"];

  if (disposition.includes("filename=")) {
    const match = disposition.match(REGEX_FILENAME_FROM_CONTENT_DEPOSITION);
    return match[1].replace(/['"]/g, "");
  }
  return "";
};

export const isRecordTypeProgram = (type: string) =>
  type ===
  PENDING_BILLING_REPORT_TYPE_PROGRAM_PROJECT_SITE_CORE_KEY_VALUE.typeProgram;

const formatPhoneNumber = (phoneNumber: {
  countryCode?: string;
  number?: string;
}) => {
  const countryCode = getValueOrBlank(phoneNumber?.countryCode);
  const number = getValueOrBlank(phoneNumber?.number);
  return isNotEmpty(number) ? `${countryCode} ${number}` : EMPTY_VALUE_DASH;
};

const stateRegionProvinceFormatter = (
  getValueByKeyOfDropdown: (
    stateSitecoreKey: string,
    valueToFind: string,
  ) => string,
  stateSitecoreKey: string,
) => {
  if (isNotEmpty(stateSitecoreKey)) {
    const stateValue = getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.stateRegionProvinceList,
      stateSitecoreKey,
    );
    return isNotEmpty(stateValue)
      ? stateValue
      : getValueOrBlank(stateSitecoreKey);
  }
  return EMPTY_VALUE_DASH;
};

export const mapOutReachReports = (
  list: OutreachReportSummary[],
  userCountryDateFormat: string,
  getValueByKeyOfDropdown: (valueKey: string, valueToFind: string) => string,
  commonLabels: KeyValue,
): OutreachReportSummaryRow[] => {
  return list.map((item) => ({
    programId: item?.programId,
    clientId: item?.clientId,
    programKey: item?.programKey,
    clientName: item?.clientName,
    participantName: item?.participantName,
    marketForDeliveryOffice: item?.marketForDeliveryOffice,
    deliveryOffice: item?.deliveryOffice,
    programTemplate: item?.programTemplate,
    outreachEligibleDate: convertToUserFormat(
      item?.outreachEligibleDate,
      userCountryDateFormat,
    ),
    nextContactDate: convertToUserFormat(
      item?.nextContactDate,
      userCountryDateFormat,
    ),
    lastActivity: convertToUserFormat(
      item?.lastActivity,
      userCountryDateFormat,
    ),
    programStatus: item?.programStatus,
    programStatusDisplayValue: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.programStatusList,
      item.programStatus,
    ),
    mustStartByDate: convertToUserFormat(
      item?.mustStartByDate,
      userCountryDateFormat,
    ),
    stateRegionProvince: stateRegionProvinceFormatter(
      getValueByKeyOfDropdown,
      getValueOrBlank(item?.stateRegionProvince?.stateId),
    ),
    contactWithin24Hrs: item?.contactWithin24Hrs
      ? commonLabels.yes
      : commonLabels.no,
    bestMatchPreference: item?.bestMatchPreference,
    alternateDisplayName: item?.alternateDisplayName,
    registrationCode: item?.registrationCode,
    registrationStatusNext: item?.registrationStatusNext,
    registrationStatusNextDisplayValue: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.registrationStatusNextList,
      item.registrationStatusNext,
    ),
    notificationSupportDate: convertToUserFormat(
      item?.notificationSupportDate,
      userCountryDateFormat,
    ),
    notificationDate: convertToUserFormat(
      item?.notificationDate,
      userCountryDateFormat,
    ),
    primaryEmailId: item?.primaryEmailId,
    alternateEmailId: item?.alternateEmailId,
    primaryPhoneNumber: formatPhoneNumber(item?.primaryPhoneNumber),
    alternatePhoneNumber: formatPhoneNumber(item?.alternatePhoneNumber),
    appendedOtherPhone: formatPhoneNumber(item?.appendedOtherPhone),
    registrationEmailId: item?.registrationEmailId,
    releaseDate: convertToUserFormat(item?.releaseDate, userCountryDateFormat),
    nisId: item?.nisId,
  }));
};

export const mapNotificationStatusReports = (
  list: NotificationStatusReportSummary[],
  userCountryDateFormat: string,
  getValueByKeyOfDropdown: (valueKey: string, valueToFind: string) => string,
): NotificationStatusReportSummaryRow[] => {
  return list.map((item) => ({
    programId: item?.programId,
    clientId: item?.clientId,
    programKey: item?.programKey,
    clientName: item?.clientName,
    participantName: item?.participantName,
    deliveryMarket: item?.deliveryMarket,
    deliveryOffice: item?.deliveryOffice,
    programTemplate: item?.programTemplate,
    alternateDisplayName: item?.alternateDisplayName,
    notificationSupportDate: convertToUserFormat(
      item?.notificationSupportDate,
      userCountryDateFormat,
    ),
    programStatus: item?.programStatus,
    programStatusDisplayValue: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.programStatusList,
      item.programStatus,
    ),
    primaryManagedStatus: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.primaryManagedStatusList,
      getValueOrBlank(item.primaryManagedStatus),
    ),
    secondaryManagedStatus: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.secondaryManagedStatusList,
      getValueOrBlank(item.secondaryManagedStatus),
    ),
    outreachEligibleStatus: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.outreachEligibleStatusList,
      getValueOrBlank(item.outreachEligibleStatus),
    ),
    stateRegionProvince: stateRegionProvinceFormatter(
      getValueByKeyOfDropdown,
      getValueOrBlank(item?.stateRegionProvince?.stateId),
    ),
    primaryEmailId: item?.primaryEmailId,
    alternatePhoneNumber: formatPhoneNumber(item?.alternatePhoneNumber),
    primaryPhoneNumber: formatPhoneNumber(item?.primaryPhoneNumber),
    registrationStatusNext: item?.registrationStatusNext,
    registrationStatusNextDisplayValue: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.registrationStatusNextList,
      item.registrationStatusNext,
    ),
  }));
};
export const mapCoachReports = (
  list: CoachReportSummary[],
): CoachReportSummaryRow[] => {
  return list.map((item) => ({
    assignedCoach: item?.assignedCoach,
    activeAssignments: item?.activeAssignments,
    openAssignments: item?.openAssignments,
    inactiveAssignments: item?.inactiveAssignments,
  }));
};

export const mapNCBPReports = (
  list: NCBPReportsResponseSummary[],
  userCountryDateFormat: string,
  getValueByKeyOfDropdown: (valueKey: string, valueToFind: string) => string,
): NCBPReportsSummaryRow[] => {
  return list.map((item) => ({
    dossierNumber: item?.dossierNumber,
    keyNumber: item?.keyNumber,
    id: isRecordTypeProgram(item?.type)
      ? getValueOrBlank(item?.programId)
      : getValueOrBlank(item?.projectId),
    isRecordTypeProgram: isRecordTypeProgram(item?.type),
    type: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.typeList,
      item?.type,
    ),
    client: item?.client?.name,
    clientId: item?.client?.id,
    clientType: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.clientTypeList,
      item?.clientType,
    ),
    programProjectName: item?.programProjectName,
    actualClient: item?.actualClient?.name,
    actualClientId: item?.actualClient?.id,
    invoiceAmount: getAmountWithCurrency(item?.invoiceAmount, item?.currency),
    invoiceLineNumber: item?.invoiceLineNumber,
    invoiceLineItemDetails: getValueByKeyOfDropdown(
      reportsResultsSiteCoreKeys.billingItemTypeList,
      item?.invoiceLineItemDetails,
    ),
    invoiceLineTotalBillingAmount: getAmountWithCurrency(
      item?.invoiceLineTotalBillingAmount,
      item?.currency,
    ),
    invoiceNumber: item?.invoiceNumber,
    invoiceDate: convertToUserFormat(item?.invoiceDate, userCountryDateFormat),
  }));
};